import { formatCurrency, formatNumber, formatPercent } from '@angular/common';
import Big from 'big.js';

export class NumberHelperService {
    /**
     * Returns a number converted from a non-number. The string representation of value may contain
     * commas.
     *
     * @param value The value to convert.
     *
     * @returns The number.
     */
    static formatNumber(value: any): number {
        return Number((value ?? 0).toString().replace(/,/g, ''));
    }

    /**
     * Returns a number converted from a non-number and rounded down and decimals
     *
     * @param value The value to convert.
     * @param roundDown determines if the number should be rounded down.
     *
     * @returns The number.
     */
    static formatRoundedDownNumber(value: any, roundDown: boolean): number {
        return roundDown
            ? new Big(this.formatNumber(value)).round(0, Big.roundDown).toNumber()
            : this.formatNumber(value);
    }

    /**
     * Returns a number rounded to the specified number of decimal places.
     *
     * @param value The number to round.
     * @param decimalPlaces The number of decimal places to round to.
     *
     * @returns The rounded number.
     */
    static formatNumberToDecimalPlace(value: number, decimalPlaces: number): number {
        return new Big(value ?? 0).round(decimalPlaces, Big.roundHalfUp).toNumber();
    }

    /**
     * Returns a string representing a number in fixed-point notation.
     *
     * @param value The number to format.
     * @param decimalPlaces Number of significant digits.
     *
     * @returns The formatted number.
     */
    static formatNumberToPrecision(value: number, decimalPlaces: number): string {
        return formatNumber(value ?? 0, 'en-US', `1.${decimalPlaces}-${decimalPlaces}`);
    }

    /**
     * Formats a number as currency.
     *
     * @param value The number to format.
     *
     * @returns The formatted currency value.
     */
    static formatCurrency(value: number): string {
        return formatCurrency(value, 'en-US', '$', 'USD');
    }

    /**
     * Formats a number as a percentage.
     *
     * @param value The number to format (examples: 0.5 = 50.00%, 1 = 100.00%)
     *
     * @returns The formatted percentage value.
     */
    static formatPercent(value: number): string {
        return formatPercent(value, 'en-US', '1.2-2');
    }
}
